import { getParentFlatName } from './getParentFlatName';

export const COLLAPSE_REVEAL_EVENT = 'knm-collapse-reveal';

export interface ICollapseRevealEventDetail {
  elementId: string;
  /** Проставляет KnmCollapse, если по этому запросу он действительно раскрылся */
  revealed?: boolean;
}

/** Столько тиков подряд без единого раскрытия - дальше ждать нечего */
const IDLE_ATTEMPTS = 2;
/** Жёсткий предохранитель от зацикливания, глубину вложенности не ограничивает */
const MAX_ATTEMPTS = 15;
/** Пауза между попытками: KnmCollapse рендерит содержимое отложенно (спиннер + setTimeout 60ms) */
const REVEAL_ATTEMPT_DELAY = 80;

export const isElementVisible = (element: HTMLElement): boolean => {
  const { checkVisibility } = element as HTMLElement & { checkVisibility?: () => boolean };

  return typeof checkVisibility === 'function' ? checkVisibility.call(element) : !!element.offsetParent;
};

/**
 * Id блоков паспорта строятся иерархически: `objects[123]` для аккордеона объекта
 * и `objects[123].objectKind` для поля внутри него (см. fieldsElementsIds).
 * Это позволяет понять, лежит ли цель внутри блока, не имея её в DOM.
 */
export const isRevealTargetInsideBlock = (elementId: string, blockId: string | undefined): boolean => {
  if (!blockId) return false;

  if (elementId === blockId) return true;

  const nextChar = elementId[blockId.length];

  return elementId.startsWith(blockId) && (nextChar === '.' || nextChar === '[');
};

/**
 * Сам элемент и его блоки-предки по той же иерархии id.
 * Нужны как запасной вариант: у части полей формы своего якоря в разметке нет
 * (например, у зеркальных полей вроде `...violations[i].title`, которые заполняет
 * соседний контрол), и тогда скроллим хотя бы к ближайшему блоку.
 */
const getElementIdCandidates = (elementId: string): string[] => {
  const candidates = [elementId];
  let path = elementId;

  while (path) {
    path = getParentFlatName(path);

    if (path) candidates.push(path);
  }

  return candidates;
};

const findExistingElement = (elementIds: string[]) =>
  elementIds.reduce<HTMLElement | null>((found, elementId) => found || document.getElementById(elementId), null);

/**
 * Просит свёрнутые аккордеоны, внутри которых лежит элемент `elementId`, раскрыться,
 * и резолвится, когда элемент появился в DOM и стал видимым.
 *
 * Запрос повторяется, потому что за один тик до цели не дойти: вложенный аккордеон
 * монтируется только после раскрытия родителя, а KnmCollapse рендерит содержимое
 * отложенно. Число повторов не привязано к глубине вложенности - цикл идёт, пока
 * каждый тик хоть что-то раскрывает (об этом сообщает сам KnmCollapse через
 * `detail.revealed`), и останавливается, когда прогресса больше нет.
 *
 * Если точного элемента так и нет - резолвится ближайшим существующим предком.
 */
export const revealCollapsedElement = (elementId: string): Promise<HTMLElement | null> =>
  new Promise(resolve => {
    let attempts = 0;
    let idleAttempts = 0;

    const tick = () => {
      // Просим раскрыться всегда, в том числе когда цель - сам аккордеон и он уже виден
      const detail: ICollapseRevealEventDetail = { elementId };

      window.dispatchEvent(
        new CustomEvent<ICollapseRevealEventDetail>(COLLAPSE_REVEAL_EVENT, { detail }),
      );

      attempts += 1;
      idleAttempts = detail.revealed ? 0 : idleAttempts + 1;

      window.setTimeout(() => {
        const element = document.getElementById(elementId);

        if (element && isElementVisible(element)) {
          resolve(element);

          return;
        }

        if (idleAttempts >= IDLE_ATTEMPTS || attempts >= MAX_ATTEMPTS) {
          resolve(element || findExistingElement(getElementIdCandidates(elementId)));

          return;
        }

        tick();
      }, REVEAL_ATTEMPT_DELAY);
    };

    tick();
  });
